import React, {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames';
import isNaN from 'lodash/isNaN';
import debounce from 'lodash/debounce';

import { ReactComponent as LocationIcon } from 'assets/images/svg/location.svg';

import container from 'containers/SearchAutocomplete';
import AutoComplete from 'components/shared/ui/Autocomplete';

import { CURRENT_LOCATION } from 'utils/constants';
import { M } from 'utils/siteStyle';

const STRING_LIMIT_SEARCH = 3;
const NUMBER_LIMIT_SEARCH = 3;

export const SearchAutocomplete = ({
  wrapperClassName,
  searchSuggestions,
  location,
  getSearchSuggestions,
  setLocation,
  currentLocation,
  initialAtCurrentLocation,
  resetSearchSuggestions,
  searchFormError,
}) => {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [searchTermAddedByUser, setSearchTermAddedByUser] = useState(false);
  const inputRef = useRef(null);

  const debouncedGetSearchSuggestions = useCallback(
    debounce(getSearchSuggestions, 500),
    [getSearchSuggestions],
  );

  const currentLocationLabel = useMemo(() => {
    if (location?.label !== 'Current Location') return location?.label;
    if (currentLocation?.city && currentLocation?.state) return `${currentLocation?.city}, ${currentLocation?.state}`;
    if (currentLocation?.city) return `${currentLocation?.city}`;
    return 'Current Location';
  }, [currentLocation, location]);

  const getItemStyle = (item, isHighlighted) => cls('!text-4 !leading-none hover:text-primary', {
    '!font-black !text-primary': item?.type === 'Current' || item?.type === 'CurrentDisabled',
    '!font-medium': item?.type === 'Recent',
    '!font-bold': item?.type === 'header',
    '!text-primary': isHighlighted && item?.type !== 'error',
  });

  const shouldSearch = (newValue) => {
    let lengthLimit = STRING_LIMIT_SEARCH;
    if (!isNaN(parseFloat(newValue))) {
      lengthLimit = NUMBER_LIMIT_SEARCH;
    }
    return (newValue.length >= lengthLimit);
  };

  const onChange = useCallback((val) => {
    setSearchTermAddedByUser(true);
    const textSearch = String(val);
    setValue(textSearch);
    if (shouldSearch(textSearch)) {
      debouncedGetSearchSuggestions(textSearch);
    }
  }, [setValue, shouldSearch, setLocation]);

  const onSelect = (val, item) => {
    if (item.type === 'error' || item.type === 'CurrentDisabled' || item.type === 'header') return;
    setLocation(item);
    setValue(item.label);
    if (inputRef.current) inputRef.current.blur();
  };

  const shouldOpenSuggestions = useMemo(
    () => (shouldSearch(value) && searchSuggestions.length > 0 && !location),
    [searchSuggestions, location, value],
  );

  useEffect(
    () => {
      if (!searchTermAddedByUser && initialAtCurrentLocation && currentLocation?.city) {
        setLocation({
          ...CURRENT_LOCATION,
          type: 'City',
          hierarchizedUrl: currentLocation.hierarchizedUrl,
          latitude: currentLocation.lat,
          longitude: currentLocation.lon,
          currentLocationSlug: currentLocation.slug,
          currentLocationLabel: currentLocation.label,
          currentLocationName: currentLocation.city,
        });
      }
    },
    [searchTermAddedByUser, initialAtCurrentLocation, currentLocation],
  );

  const handleClick = () => {
    resetSearchSuggestions();
    setSearchTermAddedByUser(true);
    setValue('');
    setLocation(null);
    setIsFocused(true);
  };

  const errorMessage = useMemo(() => {
    if (searchFormError) return (<p className="font-primary mb-2 ml-4 text-3 text-red">Please select one location</p>);
    return null;
  }, [searchFormError]);

  return (
    <div className={cls(wrapperClassName, 'relative')}>
      <div
        id="location-searchbar"
        className={cls('flex gap-4 justify-between h-14 bg-white border px-4 border-light-grey-3 rounded-3 cursor-text hover:border-blue-grey', {
          '!border-primary': isFocused || searchFormError,
          '!rounded-2': M.FOH,
          '!rounded-none': M.GD,
        })}
      >
        <div className="flex items-center">
          <LocationIcon className="w-4 h-4 text-dark fill-current" />
        </div>
        <div className="flex flex-col justify-center flex-grow">
          <label htmlFor="search-autocomplete-input" className="font-primary text-3.5 leading-4 -tracking-0.2 text-grey-3">
            Where to?
          </label>
          <AutoComplete
            name="search-autocomplete"
            open={shouldOpenSuggestions}
            items={searchSuggestions}
            inputProps={{
              ref: inputRef,
              id: 'search-autocomplete-input',
              placeholder: 'Any course, city, or zip',
              onClick: handleClick,
              onFocus: () => setIsFocused(true),
              onBlur: () => setIsFocused(false),
            }}
            value={!searchTermAddedByUser && location?.label ? currentLocationLabel : value}
            onChange={onChange}
            onSelect={onSelect}
            getItemValue={((item) => item.label)}
            styles={{
              menu: 'pt-4 border border-light-grey-four shadow-[0_0.25rem_1rem_0_rgba(10,18,69,0.2)]',
              input: cls('px-0', { 'font-medium': !M.FOH }),
            }}
            renderItemStyle={getItemStyle}
          />
        </div>
      </div>
      {errorMessage}
    </div>
  );
};

SearchAutocomplete.defaultProps = {
  wrapperClassName: '',
  location: null,
  currentLocation: null,
  initialAtCurrentLocation: false,
};

const searchAutocompleteItemProp = PropTypes.shape({
  id: PropTypes.number,
  label: PropTypes.string,
  type: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  distance: PropTypes.number,
  slug: PropTypes.string,
});

const currentLocationProp = PropTypes.shape({
  city: PropTypes.string,
  state: PropTypes.string,
  hierarchizedUrl: PropTypes.string,
  lat: PropTypes.number,
  lon: PropTypes.number,
  slug: PropTypes.string,
  label: PropTypes.string,
});

SearchAutocomplete.propTypes = {
  wrapperClassName: PropTypes.string,
  searchSuggestions: PropTypes.arrayOf(searchAutocompleteItemProp).isRequired,
  location: searchAutocompleteItemProp,
  getSearchSuggestions: PropTypes.func.isRequired,
  setLocation: PropTypes.func.isRequired,
  currentLocation: currentLocationProp,
  initialAtCurrentLocation: PropTypes.bool,
  resetSearchSuggestions: PropTypes.func.isRequired,
  searchFormError: PropTypes.bool.isRequired,
};

export default container(SearchAutocomplete);
